import type { SignatureTask } from '@seen/core/models/tasks/signature/signatureTask'
import { hasReference } from '@seen/core/utils'
import { Badge, Button } from '@seen/ui'
import { IconWriting } from '@tabler/icons-react'
import type { JSX } from 'react'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

export function SignatureStatus({
  signature,
  isReview,
  withConfirmation,
  onSigned,
  allowSignOnBehalf,
}: {
  signature: SignatureTask
  isReview?: boolean
  withConfirmation?: boolean
  onSigned?: (signature: SignatureTask) => void
  allowSignOnBehalf?: boolean
}): JSX.Element | null {
  const currentPractitioner = useCurrentPractitioner()
  const showAdminActions = usePortalStore((state) => state.showAdminActions)

  if (signature.state === 'todo') {
    if (
      hasReference(signature.ownerPractitioner, currentPractitioner.reference)
    ) {
      return (
        <SignatureButton
          signature={signature}
          label={isReview ? 'Review' : 'Sign'}
          onSigned={onSigned}
          withConfirmation={withConfirmation}
          confirmationTitle={isReview ? 'Create Review' : 'Confirm Signature'}
          confirmationDescription={
            isReview
              ? 'Include a review note:'
              : 'Are you sure you want to sign this?'
          }
        />
      )
    } else if (
      showAdminActions ||
      allowSignOnBehalf ||
      (signature.roles && currentPractitioner.isRole(...signature.roles))
    ) {
      return (
        <SignatureButton
          signature={signature}
          label={isReview ? 'Review on Behalf' : 'Sign on Behalf'}
          onSigned={onSigned}
          withConfirmation={withConfirmation}
          confirmationTitle={isReview ? 'Create Review' : 'Confirm Signature'}
          confirmationDescription={
            isReview
              ? 'Include a review note:'
              : 'Are you sure you want to sign this?'
          }
        />
      )
    } else {
      return (
        <Badge variant="outline">
          {isReview ? 'Review Needed' : 'Awaiting Signature'}
        </Badge>
      )
    }
  } else if (signature.isDone) {
    if (isReview) {
      return (
        <Badge variant="outline">
          <IconWriting className="h-4 w-4" />
          Review Done
        </Badge>
      )
    }

    if (signature.isSignedOnBehalf) {
      return (
        <Badge variant="outline">
          <IconWriting className="h-4 w-4" />
          <div className="flex items-center gap-2">
            Signed on behalf by
            <PractitionerDisplay practitioner={signature.ownerPractitioner} />
          </div>
        </Badge>
      )
    }

    return (
      <Badge variant="outline">
        <IconWriting className="h-4 w-4" />
        Signed
      </Badge>
    )
  } else {
    return null
  }
}

type SignatureButtonProps = {
  signature: SignatureTask
  label: string
  onSigned?: (signature: SignatureTask) => void
} & (
  | {
      withConfirmation?: false
    }
  | {
      withConfirmation: true
      confirmationTitle: string
      confirmationDescription: string
    }
)

export function SignatureButton({
  signature,
  label,
  ...props
}: SignatureButtonProps): JSX.Element {
  const patch = usePatchMutation<SignatureTask>()
  const currentPractitioner = useCurrentPractitioner()

  const { open: confirmWithAnnotation } = useConfirmWithAnnotationStore()

  const handleSign = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation()
    e.preventDefault()
    if (props.withConfirmation) {
      confirmWithAnnotation({
        title: props.confirmationTitle,
        description: props.confirmationDescription,
        onConfirm: (note) => {
          patch.mutate(
            {
              instance: signature,
              mutateFn: (patchInstance) => {
                patchInstance.sign(currentPractitioner.reference)
                if (note) {
                  patchInstance.description = note
                }
              },
            },
            {
              onSuccess: (signature) => {
                props.onSigned?.(signature)
              },
            }
          )
        },
        isRichText: true,
      })
    } else {
      patch.mutate(
        {
          instance: signature,
          mutateFn: (patchInstance) => {
            patchInstance.sign(currentPractitioner.reference)
          },
        },
        {
          onSuccess: (signature) => {
            props.onSigned?.(signature)
          },
        }
      )
    }
  }

  return (
    <Button
      variant="outline"
      color="green"
      size="sm"
      loading={patch.isPending}
      onClick={handleSign}
    >
      <IconWriting className="mr-2 h-4 w-4" />
      {label}
    </Button>
  )
}
